import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "../utils/cx";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  /** `md` (44px) is the accessible default; `sm` keeps a 44px hit area (§14.3). */
  readonly size?: ButtonSize;
  /** Shows a spinner, keeps width, disables interaction, sets aria-busy. */
  readonly loading?: boolean;
  readonly iconStart?: ReactNode;
  readonly iconEnd?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    iconStart,
    iconEnd,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cx("ui-btn", `ui-btn--${variant}`, `ui-btn--${size}`, loading && "ui-btn--loading", className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span className="ui-btn-content" aria-hidden={loading || undefined}>
        {iconStart}
        {children}
        {iconEnd}
      </span>
      {loading ? (
        <span className="ui-btn-spinner-holder">
          <Spinner />
        </span>
      ) : null}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, "iconStart" | "iconEnd" | "children"> {
  /** Screen-reader label — required: icon-only controls are never unnamed (§14.3). */
  readonly label: string;
  readonly children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, className, children, variant = "ghost", ...rest },
  ref,
) {
  return (
    <Button ref={ref} variant={variant} className={cx("ui-btn--icon", className)} aria-label={label} {...rest}>
      {children}
    </Button>
  );
});
